import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useHeader } from './../../components/HeaderContext';
import { useAxiosPrivate } from "./../../hooks/useAxiosPrivate";
import useAuth from "./../../hooks/useAuth.js";
import { useAlert } from './../../components/AlertContext.jsx';
import { format } from "date-fns";

const ProductsView = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const { auth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const { triggerAlert } = useAlert();
    const { setHeader } = useHeader();

    const ReturnPage = "/products";
    const last_modified_date = format(Date(), "dd-MM-yyyy HH:mm:ss");
    const last_modified_user_id = auth.user_id ? auth.user_id : 0;

    // Initial form state
    const initialFormData = {
        product_id: '',
        code: '',
        code2: '',
        description: '',
        description2: '',
        unit_of_measure: '',
        tariff_code: '',
        blocked: false,
        status: '',
        item_category_code: '',
        macrolevel: '',
        division: '',
        item_type: '',
        composition: '',
        vert_component_group: '',
        horz_component_group: '',
        collection: '',
        line: '',
        brand: '',
        routing_code: '',
        production_bom_code: '',
        company_id: '',
        last_modified_date: last_modified_date,
        last_modified_user_id: last_modified_user_id
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    useEffect(() => {
        console.log("View page location state:", location.state);
    }, [location.state]);

    // Set page header
    useEffect(() => {
        setHeader("View Product");
    }, [setHeader]);

    // Fetch data when component mounts
    useEffect(() => {
        const fetchDataForForm = async () => {
            try {
                setLoading(true);
                const response = await axiosPrivate.get(`${baseURL}/products/${id}`);
                const productData = response.data;

                // Format the last modified date if it exists
                const formattedDate = productData.last_modified_date
                    ? `${productData.last_modified_date.substring(0, 10)} ${productData.last_modified_date.substring(11, 19)}`
                    : last_modified_date;

                setFormData({
                    ...productData,
                    last_modified_date: formattedDate,
                    last_modified_user_id: productData.last_modified_user_id || last_modified_user_id
                });
                setLoading(false);
            } catch (error) {
                console.error('Error fetching product data:', error);
                setError('Failed to load product data');
                setLoading(false);
                triggerAlert('Failed to load product data', 'error');
            }
        };

        fetchDataForForm();
    }, [id, axiosPrivate, baseURL, triggerAlert]);

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        try {
            const updatedFormData = {
                ...formData,
                last_modified_user_id: last_modified_user_id
            };

            await axiosPrivate.put(`${baseURL}/products/${id}`, updatedFormData);
            triggerAlert('Record Saved!', 'success');
            navigate(ReturnPage);
        } catch (error) {
            console.error('Error updating product:', error);
            const message = error.response?.data?.err?.originalError?.info?.message
                ? `Error saving record: ${error.response.data.err.originalError.info.message}`
                : 'Error saving record';
            triggerAlert(message, 'error');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (loading) return <div className="text-center mt-4">Loading...</div>;
    if (error) return <div className="alert alert-danger mt-4">{error}</div>;

    // Enhanced field configuration with layout control
    const fieldGroups = [
        {
            title: "Basic Information",
            layout: 'flex', // 'flex' for horizontal, 'stack' for vertical
            fields: [
                { name: 'product_id', label: 'Product ID', type: 'text', disabled: true, size: 1, hidden: true },
                { name: 'code', label: 'Code', type: 'text', required: true, size: 2 },
                { name: 'code2', label: 'Code 2', type: 'text', size: 2 },
            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'description', label: 'Description', type: 'text', required: true, size: 4, },
                { name: 'description2', label: 'Description 2', type: 'text', size: 4 }
            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'unit_of_measure', label: 'Unit of Measure', type: 'text', size: 1 },
                { name: 'tariff_code', label: 'Tariff Code', type: 'text', size: 1 },
            ]
        },
        {
            title: "Classification",
            layout: 'flex',
            fields: [
                { name: 'macrolevel', label: 'Macrolevel', type: 'text', size: 1 },
                { name: 'brand', label: 'Brand', type: 'text', size: 2 },
                { name: 'division', label: 'Division', type: 'text', size: 1 },
                { name: 'item_category_code', label: 'Category Code', type: 'text', size: 2 },
                { name: 'item_type', label: 'Item Type', type: 'text', size: 2 }
            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'routing_code', label: 'Routing Code', type: 'text', size: 2 },
                { name: 'production_bom_code', label: 'Production Bom', type: 'text', size: 2 },
            ]
        },
        {
            fields: [
                { name: 'composition', label: 'Composition', type: 'text', size: 3, hidden: true }
            ]
        },
        {
            title: "System Information",
            layout: 'flex',
            fields: [
                { name: 'last_modified_date', label: 'Last Modified', type: 'text', disabled: true, size: 2 },
                { name: 'last_modified_user_id', label: 'Modified By', type: 'text', disabled: true, size: 1 }
            ]
        }
    ];

    const renderField = (field) => {
        if (field.hidden) return null;

        const sizeClass = `col-md-${field.size || 1}`;
        const inputClass = `form-control form-control-sm app_font1 ${field.disabled ? 'bg-light' : ''}`;
        const labelClass = `form-label app_font1 ${field.required ? 'required' : ''}`;

        return (
            <div className={`${sizeClass} mb-3`} key={field.name}>
                {/* Label - not shown for checkboxes */}
                {field.type !== 'checkbox' && (
                    <label className={labelClass}>
                        {field.label}
                    </label>
                )}

                {/* SELECT Input */}
                {field.type === 'select' ? (
                    <select
                        className={inputClass}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleInputChange}
                        disabled={field.disabled}
                    >
                        {field.placeholder && <option value="">{field.placeholder}</option>}
                        {field.options?.map(option => (
                            typeof option === 'string'
                                ? <option key={option} value={option}>{option}</option>
                                : <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                ) :

                    /* CHECKBOX Input */
                    field.type === 'checkbox' ? (
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                name={field.name}
                                checked={!!formData[field.name]}
                                onChange={handleInputChange}
                                disabled={field.disabled}
                                id={`checkbox-${field.name}`}
                            />
                            <label className="form-check-label app_font1" htmlFor={`checkbox-${field.name}`}>
                                {field.label}
                            </label>
                        </div>
                    ) :

                        /* TEXTAREA Input */
                        field.type === 'textarea' ? (
                            <textarea
                                className={inputClass}
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                disabled={field.disabled}
                                rows={field.rows || 3}
                                placeholder={field.placeholder}
                            />
                        ) :

                            /* DATE Input */
                            field.type === 'date' ? (
                                <input
                                    type="date"
                                    className={inputClass}
                                    name={field.name}
                                    value={formData[field.name] || ''}
                                    onChange={handleInputChange}
                                    disabled={field.disabled}
                                />
                            ) :

                                /* NUMBER Input */
                                field.type === 'number' ? (
                                    <input
                                        type="number"
                                        className={inputClass}
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleInputChange}
                                        disabled={field.disabled}
                                        min={field.min}
                                        max={field.max}
                                        step={field.step}
                                        placeholder={field.placeholder}
                                    />
                                ) :

                                    /* DEFAULT Text Input */
                                    (
                                        <input
                                            type={field.type || 'text'}
                                            className={inputClass}
                                            name={field.name}
                                            value={formData[field.name] || ''}
                                            onChange={handleInputChange}
                                            disabled={field.disabled}
                                            placeholder={field.placeholder}
                                            maxLength={field.maxLength}
                                        />
                                    )}

                {/* Field description/help text */}
                {field.helpText && (
                    <small className="form-text text-muted">{field.helpText}</small>
                )}
            </div>
        );
    };


    const handleCancel = () => {

        console.log("Current pagination state:", {
            page: location.state?.pagination?.page
        });

        navigate(ReturnPage, {
            state: {
                pagination: {
                    page: location.state?.pagination?.page || 0,
                    pageSize: location.state?.pagination?.pageSize || 20,
                    sortField: location.state?.pagination?.sortField || "code",
                    sortOrder: location.state?.pagination?.sortOrder || "asc"
                }
            }
        });
    };


    return (
        <div className="d-flex align-items-start mt-1">
            <div className="p-2 rounded w-100">
                <form onSubmit={handleSubmitEdit}>
                    {fieldGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className={group.title ? 'mt-4' : ''}>
                            {group.title && <h5 className="border-bottom pb-2">{group.title}</h5>}
                            <div className={`row ${group.layout === 'stack' ? 'flex-column' : ''}`}>
                                {group.fields.map(field => renderField(field))}
                            </div>
                        </div>
                    ))}

                    <div className="mt-3">
                        <button type='submit' className="btn btn-primary me-2">Save</button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductsView;